import React, { useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid-pro";
import { ScanButton } from "components/Picklist/common/ScanButton";
import { api } from "services/api.service";
import { ShipstationOrderItem } from "types/shipstationOrderItem";

export function CustomerReturnActions({ row }: { row: ShipstationOrderItem }) {
  const [open, setOpen] = useState(false);
  const [inventoryId, setInventoryId] = useState<string>();
  const [location, setLocation] = useState<string>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const apiRef = useGridApiContext();
  const retry = ["cancelled", "skipped"].includes(row.state);
  if (row.state === "packed" || row.returnStatus === "returned") return null;

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const response = await api.post(`/customer_return_items/${row.id}/${retry ? "retry_return" : "cancel"}`, {
        inventoryId,
        warehouseLocation: location,
      });
      apiRef.current.updateRows([response.data]);
      setOpen(false);
      setInventoryId(undefined);
      setLocation(undefined);
    } catch (err) {
      const failure = err as { response?: { data?: { errors?: string[] } } };
      setError(failure.response?.data?.errors?.[0] || "Unable to update this return. Please retry.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        onClick={() => {
          setInventoryId(undefined);
          setLocation(undefined);
          setError(undefined);
          setOpen(true);
        }}
      >
        {retry ? "Retry return" : "Cancel return"}
      </Button>
      <Dialog open={open} onClose={() => !busy && setOpen(false)}>
        <DialogTitle>{retry ? "Retry the same return" : "Cancel return fulfillment"}</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography>The device stays reserved for this return and its hold remains pending.</Typography>
            {retry && (
              <>
                <Typography>
                  Scan Inventory {row.inventoryId}, then move it to its client’s active quarantine location.
                </Typography>
                <ScanButton
                  disabled={busy}
                  onScan={(value) => {
                    if (value !== String(row.inventoryId)) {
                      setInventoryId(undefined);
                      setLocation(undefined);
                      setError("Scan the original return device. Replacements are not allowed.");
                    } else {
                      setInventoryId(value);
                      setError(undefined);
                    }
                  }}
                >
                  {inventoryId ? "Device confirmed" : "Scan original device"}
                </ScanButton>
                <ScanButton disabled={busy || !inventoryId} onScan={(value) => setLocation(value || undefined)}>
                  {location || "Scan client quarantine location"}
                </ScanButton>
              </>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setOpen(false)}>
            Back
          </Button>
          <Button disabled={busy || (retry && (!inventoryId || !location))} onClick={submit}>
            {retry ? "Retry same return" : "Keep pending and cancel fulfillment"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
